// Exact money and quantity arithmetic.
//
// Money is INR held as integer paise in a bigint (MongoDB Int64 on disk). It
// crosses the API as a decimal string of paise ("234780" = ₹2,347.80) so no
// JSON number ever carries a value that a float could round.
//
// Quantities are fixed-precision decimals held as integer thousandths
// (QTY_SCALE = 1000), e.g. 22.825 litres = 22825n. They cross the API as a
// decimal string with three places ("22.825").
//
// Rounding rule (tested): amounts derived from quantity × unit price are
// rounded half-up to the nearest paisa. All derived amounts are non-negative.

export type Paise = bigint;
export type QtyMilli = bigint;

export const QTY_SCALE = 1000n;
export const QTY_DECIMALS = 3;

export class MoneyFormatError extends Error {}

/** Integer division of non-negative values, rounding half up. */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n) throw new RangeError('denominator must be positive');
  if (numerator < 0n) throw new RangeError('numerator must be non-negative');
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** Integer division of non-negative values, rounding up. */
export function divCeil(numerator: bigint, denominator: bigint): bigint {
  if (denominator <= 0n || numerator < 0n) throw new RangeError('non-negative operands required');
  return (numerator + denominator - 1n) / denominator;
}

/** Amount in paise for a quantity (thousandths) at a unit price in paise. */
export function amountForQuantity(quantity: QtyMilli, unitPricePaise: Paise): Paise {
  return divRoundHalfUp(quantity * unitPricePaise, QTY_SCALE);
}

/**
 * Largest quantity (thousandths) whose rounded amount does not exceed the
 * given rupee exposure. Used to show "about N litres" for a rupee indent.
 */
export function maxQuantityForAmount(amount: Paise, unitPricePaise: Paise): QtyMilli {
  if (unitPricePaise <= 0n) return 0n;
  let q = (amount * QTY_SCALE) / unitPricePaise;
  while (amountForQuantity(q + 1n, unitPricePaise) <= amount) q += 1n;
  while (q > 0n && amountForQuantity(q, unitPricePaise) > amount) q -= 1n;
  return q;
}

export function minBig(...values: bigint[]): bigint {
  return values.reduce((a, b) => (b < a ? b : a));
}
export function maxBig(...values: bigint[]): bigint {
  return values.reduce((a, b) => (b > a ? b : a));
}

const PAISE_RE = /^-?\d{1,18}$/;
const RUPEES_RE = /^(-)?(\d{1,16})(?:\.(\d{1,2}))?$/;
const QTY_RE = /^(\d{1,12})(?:\.(\d{1,3}))?$/;

/** Parse the API wire form: a decimal string of integer paise. */
export function paiseFromWire(value: unknown): Paise {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string' && PAISE_RE.test(value)) return BigInt(value);
  if (typeof value === 'number' && Number.isSafeInteger(value)) return BigInt(value);
  throw new MoneyFormatError('Amount must be a whole number of paise');
}

export function paiseToWire(value: Paise): string {
  return value.toString();
}

/** Parse a rupee amount typed by a person, e.g. "2,347.8" → 234780n. */
export function parseRupees(input: string): Paise {
  const cleaned = input.replace(/[₹,\s]/g, '');
  const m = RUPEES_RE.exec(cleaned);
  if (!m) throw new MoneyFormatError('Enter an amount like 2500 or 2500.50');
  const whole = BigInt(m[2]);
  const frac = BigInt((m[3] ?? '').padEnd(2, '0'));
  const v = whole * 100n + frac;
  return m[1] ? -v : v;
}

/** Rupees with two decimals and no grouping, e.g. "2347.80". */
export function paiseToRupeeString(value: Paise): string {
  const neg = value < 0n;
  const abs = neg ? -value : value;
  const whole = abs / 100n;
  const frac = (abs % 100n).toString().padStart(2, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

/** Indian digit grouping: 1234567 → "12,34,567". */
export function groupIndian(digits: string): string {
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  let rest = digits.slice(0, -3);
  const parts: string[] = [];
  while (rest.length > 2) {
    parts.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) parts.unshift(rest);
  return `${parts.join(',')},${last3}`;
}

/** "₹1,00,000.00" style. Accepts bigint paise or the wire string. */
export function formatINR(value: Paise | string, opts: { symbol?: boolean; paise?: boolean } = {}): string {
  const v = typeof value === 'bigint' ? value : paiseFromWire(value);
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const whole = groupIndian((abs / 100n).toString());
  const frac = (abs % 100n).toString().padStart(2, '0');
  const showPaise = opts.paise ?? true;
  const body = showPaise ? `${whole}.${frac}` : whole;
  return `${neg ? '-' : ''}${opts.symbol === false ? '' : '₹'}${body}`;
}

/** Parse a quantity like "22.825" into thousandths. */
export function parseQuantity(input: string): QtyMilli {
  const cleaned = String(input).replace(/[,\s]/g, '');
  const m = QTY_RE.exec(cleaned);
  if (!m) throw new MoneyFormatError('Enter a quantity with up to 3 decimal places, like 22.825');
  return BigInt(m[1]) * QTY_SCALE + BigInt((m[2] ?? '').padEnd(3, '0'));
}

export function quantityToWire(value: QtyMilli): string {
  const whole = value / QTY_SCALE;
  const frac = (value % QTY_SCALE).toString().padStart(3, '0');
  return `${whole}.${frac}`;
}

export function quantityFromWire(value: unknown): QtyMilli {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'string') return parseQuantity(value);
  throw new MoneyFormatError('Quantity must be a decimal string');
}

/** "22.825 L" — litres always show three decimals. */
export function formatQuantity(value: QtyMilli | string, unit = 'L'): string {
  const v = typeof value === 'bigint' ? value : quantityFromWire(value);
  return `${quantityToWire(v)} ${unit}`;
}
